import React, { useState, useEffect } from 'react';
import { Clock, Plus, Minus, AlertCircle, RefreshCw } from 'lucide-react';

const BentoTimeAdjuster: React.FC = () => {
  const [busyTimeDelay, setBusyTimeDelay] = useState<number>(0);
  const [timeSlotInterval, setTimeSlotInterval] = useState<number>(15);
  const [showNotification, setShowNotification] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    // サーバーから初期値を取得
    const loadBusyTime = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'https://line.wa-ton.com/api';
        const response = await fetch(`${API_URL}/bento-orders/busy-time`);
        const data = await response.json();

        if (data.success && data.data) {
          const serverDelay = data.data.additional_minutes || 0;
          setBusyTimeDelay(serverDelay);
          localStorage.setItem('busyTimeDelay', serverDelay.toString());
          setIsBusy(serverDelay > 0);
          console.log('✅ サーバーから繁忙時間を取得:', serverDelay);
        }
      } catch (error) {
        console.error('❌ 繁忙時間の取得に失敗（localStorageから取得）:', error);
        // フォールバック: localStorageから取得
        const savedDelay = parseInt(localStorage.getItem('busyTimeDelay') || '0');
        setBusyTimeDelay(savedDelay);
        setIsBusy(savedDelay > 0);
      }

      // 時間刻み設定はlocalStorageから取得（ローカル設定）
      const savedInterval = parseInt(localStorage.getItem('timeSlotInterval') || '15');
      setTimeSlotInterval(savedInterval);
    };

    loadBusyTime();
  }, []);

  const adjustTime = async (minutes: number) => {
    const newDelay = Math.max(0, Math.min(120, busyTimeDelay + minutes)); // 最大120分まで
    setBusyTimeDelay(newDelay);
    localStorage.setItem('busyTimeDelay', newDelay.toString());
    setIsBusy(newDelay > 0);

    // サーバーに保存
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://line.wa-ton.com/api';
      await fetch(`${API_URL}/bento-orders/busy-time`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional_minutes: newDelay })
      });
      console.log('✅ 繁忙時間をサーバーに保存:', newDelay);
    } catch (error) {
      console.error('❌ 繁忙時間の保存に失敗:', error);
    }

    // 通知表示
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);

    // 全LIFFアプリに通知（同期用）
    window.dispatchEvent(new CustomEvent('busyTimeChanged', {
      detail: { delay: newDelay }
    }));
  };

  const resetTime = async () => {
    setBusyTimeDelay(0);
    localStorage.setItem('busyTimeDelay', '0');
    setIsBusy(false);

    // サーバーに保存
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://line.wa-ton.com/api';
      await fetch(`${API_URL}/bento-orders/busy-time`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional_minutes: 0 })
      });
      console.log('✅ 繁忙時間をリセット（サーバーに保存）');
    } catch (error) {
      console.error('❌ 繁忙時間のリセットに失敗:', error);
    }

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);

    window.dispatchEvent(new CustomEvent('busyTimeChanged', {
      detail: { delay: 0 }
    }));
  };

  const changeTimeSlotInterval = (interval: number) => {
    setTimeSlotInterval(interval);
    localStorage.setItem('timeSlotInterval', interval.toString());

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);

    window.dispatchEvent(new CustomEvent('timeSlotIntervalChanged', {
      detail: { interval }
    }));
  };

  const getStatusText = () => {
    if (busyTimeDelay === 0) {
      return '通常営業';
    } else if (busyTimeDelay <= 30) {
      return 'やや混雑';
    } else if (busyTimeDelay <= 60) {
      return '混雑中';
    } else {
      return '大変混雑';
    }
  };

  const getStatusColor = () => {
    if (busyTimeDelay === 0) return 'bg-green-100 text-green-800 border-green-300';
    if (busyTimeDelay <= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (busyTimeDelay <= 60) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const calculateActualPrepTime = (baseQuantity: number) => {
    let baseTime;
    if (baseQuantity <= 2) {
      baseTime = 15;
    } else if (baseQuantity <= 5) {
      baseTime = 30;
    } else {
      baseTime = 30;
      const extraItems = baseQuantity - 5;
      baseTime += Math.ceil(extraItems / 3) * 15;
    }
    return baseTime + busyTimeDelay;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          弁当調理時間調整
        </h3>
        <div className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor()}`}>
          {getStatusText()}
        </div>
      </div>

      {/* 現在の設定 */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="text-sm text-gray-600 mb-1">現在の追加時間</div>
        <div className="text-2xl font-bold text-purple-600">
          {busyTimeDelay > 0 ? `+${busyTimeDelay}分` : '標準'}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          （基本30分 + 追加{busyTimeDelay}分）
        </div>
      </div>

      {/* 調整ボタン */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <button
          onClick={() => adjustTime(-15)}
          disabled={busyTimeDelay === 0}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg font-semibold transition-all ${
            busyTimeDelay === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600 active:scale-95'
          }`}
        >
          <Minus className="w-4 h-4" />
          15分短縮
        </button>
        
        <button
          onClick={resetTime}
          disabled={busyTimeDelay === 0}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg font-semibold transition-all ${
            busyTimeDelay === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-500 text-white hover:bg-gray-600 active:scale-95'
          }`}
        >
          <RefreshCw className="w-4 h-4" />
          リセット
        </button>

        <button
          onClick={() => adjustTime(15)}
          disabled={busyTimeDelay >= 120}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg font-semibold transition-all ${
            busyTimeDelay >= 120
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-red-500 text-white hover:bg-red-600 active:scale-95'
          }`}
        >
          <Plus className="w-4 h-4" />
          15分延長
        </button>
      </div>

      {/* サンプル計算表示 */}
      <div className="border-t pt-3">
        <div className="text-xs text-gray-600 mb-2">調理時間の目安</div>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">1-2個：</span>
            <span className="font-semibold">{calculateActualPrepTime(2)}分</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">3-5個：</span>
            <span className="font-semibold">{calculateActualPrepTime(5)}分</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">6-8個：</span>
            <span className="font-semibold">{calculateActualPrepTime(8)}分</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">9-11個：</span>
            <span className="font-semibold">{calculateActualPrepTime(11)}分</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">12-14個：</span>
            <span className="font-semibold">{calculateActualPrepTime(14)}分</span>
          </div>
        </div>
      </div>

      {/* 時間テーブル間隔設定 */}
      <div className="mt-4 pt-4 border-t">
        <div className="text-sm font-semibold text-gray-700 mb-2">受付時間の刻み設定</div>
        <div className="grid grid-cols-3 gap-2">
          {[5, 10, 15].map((interval) => (
            <button
              key={interval}
              onClick={() => changeTimeSlotInterval(interval)}
              className={`px-3 py-2 rounded-lg font-semibold transition-all ${
                timeSlotInterval === interval
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {interval}分刻み
            </button>
          ))}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          例: 5分刻みなら12:07→12:10に切り上げ
        </div>
      </div>

      {/* 注意事項 */}
      {isBusy && (
        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="text-xs text-amber-700">
              混雑時設定が有効です。LINEからの注文で自動的に
              {busyTimeDelay}分追加されます。
            </div>
          </div>
        </div>
      )}

      {/* 通知 */}
      {showNotification && (
        <div className="fixed bottom-4 right-4 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
          調理時間を更新しました
        </div>
      )}
    </div>
  );
};

export default BentoTimeAdjuster;